import type { UseFetchOptions } from '#app'
import { defu } from 'defu'

export interface IFetchResponse<T> extends Response {
  _data?: T
}

/**
 * Obtém os parâmetros para uma requisição de fetch, incluindo cabeçalhos de autenticação se disponíveis.
 *
 * @template T - O tipo dos dados esperados na resposta da requisição.
 * @param {string} url - A URL para a qual a requisição será feita.
 * @param {UseFetchOptions<T>} [options={}] - Opções adicionais para a requisição de fetch.
 * @returns {UseFetchOptions<T>} - As opções de fetch combinadas com os cabeçalhos de autenticação, se aplicável.
 */
function getParams<T>(url: string, options: UseFetchOptions<T> = {}): any {
  const authStore = useAuthStore()
  const route = useRoute()

  const defaultOptions: UseFetchOptions<T> = {
    key: url,
    headers: authStore.accessToken
      ? { Authorization: `Bearer ${authStore.accessToken}` }
      : {},
    onRequest: async () => {
      // Aguarda se há renovação em progresso
      if (authStore.isRenewingToken && authStore.renewTokenPromise) {
        await authStore.renewTokenPromise
      }
    },
    onResponseError: async (error: any) => {
      if (error.response && error.response.status === 400) {
        const organizationId = route.params.organizationId as string

        if (
          error.response._data.ErrorCode === 10041 ||
          error.response._data.errorCode === 10041
        ) {
          navigateTo({
            name: 'errors-notAllowed-organizationId',
            params: { organizationId },
          })
          throw error
        }
      }

      if (error.response && error.response.status === 401) {
        // Evita renovar para requests de token
        if (!authStore.refreshToken || url.includes('/token/renew')) {
          authStore.logout()
          navigateTo({ name: '/login' })
          throw error
        }

        // Se já está renovando, aguarda e relança o erro para retry
        if (authStore.isRenewingToken && authStore.renewTokenPromise) {
          try {
            await authStore.renewTokenPromise
            // Relança o erro para tanStack fazer retry com novo token
            throw error
          } catch (renewError) {
            authStore.logout()
            navigateTo({ name: '/login' })
            throw renewError
          }
        }

        // Inicia primeira renovação
        authStore.renewToken()

        if (authStore.renewTokenPromise) {
          try {
            await authStore.renewTokenPromise
            // Relança o erro para tanStack fazer retry com novo token
            throw error
          } catch (renewError) {
            authStore.logout()
            navigateTo({ name: '/login' })
            throw renewError
          }
        }
      }
    },
  }

  return defu(options, defaultOptions)
}

/**
 * Executa uma requisição HTTP e retorna apenas os dados da resposta.
 *
 * @template T - O tipo de dado esperado na resposta da requisição.
 * @param {string} url - A URL para a qual a requisição será enviada.
 * @param {UseFetchOptions<T>} [options={}] - Opções adicionais para a requisição.
 * @returns {Promise<T>} - Os dados da resposta da requisição.
 */
export function useFetchInstance<T>(
  url: string,
  options: UseFetchOptions<T> = {},
) {
  const params = getParams(url, options)

  return $fetch<T>(url, params)
}

// export function useFetchInstance<T>(
//   url: string,
//   options: UseFetchOptions<T> = {}
// ) {
//   const params = getParams(url, options)
//   const authStore = useAuthStore()

//   return new Promise<T>((resolve, reject) => {
//     $fetch<T>(url, params)
//       .then((response) => resolve(response as T))
//       .catch((error) => {
//         if (error.response.status === 401) {
//           if (authStore.refreshToken && url.indexOf('/token/renew') === -1) {
//             authStore.renewToken()
//             if (authStore.renewTokenPromise) {
//               const teste = authStore.renewTokenPromise as Promise<void>
//               teste
//                 .then(() => {
//                     $fetch<T>(url, getParams(url, options))
//                       .then((response) => resolve(response as T))
//                       .catch((err: any) => reject(err))
//                 })
//                 .catch(() => {
//                   authStore.logout()
//                   navigateTo({ name: 'login' })
//                   reject()
//                 })
//             }
//           } else {
//             authStore.logout()
//             navigateTo({ name: 'login' })
//             reject(error)
//           }
//         } else {
//           reject(error)
//         }
//       })
//   })
// }

/**
 * Executa uma requisição HTTP e retorna a resposta completa, incluindo cabeçalhos e status.
 *
 * @template T - O tipo de dado esperado na resposta da requisição.
 * @param {string} url - A URL para a qual a requisição será enviada.
 * @param {UseFetchOptions<T>} [options={}] - Opções adicionais para a requisição.
 */
export function useFetchInstanceRaw<T>(
  url: string,
  options: UseFetchOptions<T> = {},
) {
  const params = getParams(url, options)

  return $fetch.raw<T>(url, params)
}
